import React, { useEffect, useState } from 'react';
import SafeIcon, { HelpHint } from '../common/SafeIcon';
import {
  FiServer, FiCheckCircle, FiActivity, FiSend, FiRefreshCw,
  FiInfo, FiAlertCircle
} from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [apps, setApps] = useState([]);
  const [testAppId, setTestAppId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [response, appResponse] = await Promise.all([gatewayApi.listProviders(), gatewayApi.listApps()]);
      setProviders(response.data || []);
      setApps((appResponse.data || []).filter(app => app.status === 'active'));
    } catch (err) {
      setError(err.message || 'Unable to load providers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateMode = async (provider, mode) => {
    if(mode === 'Backup' || !provider.name.toLowerCase().includes('emailit')) {showToast('Only Emailit sending is configured. Backup routing is not active.','warning');return;}
    try {
      await gatewayApi.updateProvider({
        id: provider.id,
        name: provider.name,
        status: mode === 'Primary' ? 'active' : 'ready',
        mode,
        notes: provider.notes || ''
      });
      showToast(`${provider.name} updated to ${mode}.`);
      await load();
    } catch (err) {
      showToast(err.message || 'Unable to update provider.', 'error');
    }
  };

  const testProvider = async (provider) => {
    if(!provider.name.toLowerCase().includes('emailit')) {showToast('Provider not connected to a sending API.','warning');return;}
    if(!testAppId) {showToast('Select a registered application before testing.','warning');return;}
    const to = window.prompt(`Send a ${provider.name} test email to:`);
    if (!to) return;
    try {
      await gatewayApi.testProvider(to.trim(), testAppId);
      showToast('Provider test accepted for delivery.');
    } catch (err) {
      showToast(err.message || 'Provider test failed.', 'error');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Email Providers</h2>
          <p className="text-sm text-slate-500">Provider routing metadata is stored in PocketBase. Provider secrets remain in PocketHost Secrets.</p>
        </div>
        <button onClick={load} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 flex items-center gap-2 hover:bg-slate-50">
          <SafeIcon icon={FiRefreshCw} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-4">
        <SafeIcon icon={FiInfo} className="text-blue-500 text-xl shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800 leading-relaxed">
          Emailit is currently the only implemented sending provider. Failover is not enabled. The Emailit API key is never exposed to the browser; it remains a PocketHost Secret used only by the PocketBase gateway hook.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <label className="block text-xs font-bold text-slate-700 mb-2" htmlFor="provider-test-app">
          Application used for delivery tests
        </label>
        <select id="provider-test-app" className="w-full sm:max-w-lg rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={testAppId} onChange={event => setTestAppId(event.target.value)}>
          <option value="">Select an active BPA application</option>
          {apps.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
        </select>
        <p className="text-xs text-slate-500 mt-2">Tests are queued under the selected application's actual PocketBase account.</p>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
          Loading providers from PocketBase...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {providers.map(provider => {
            const isPrimary = provider.mode === 'Primary';
            const isBackup = provider.mode === 'Backup';
            return (
              <div key={provider.id} className={`bg-white rounded-2xl border-2 transition-all ${isPrimary ? 'border-blue-500 shadow-md ring-4 ring-blue-500/5' : 'border-slate-200'}`}>
                <div className="p-5 border-b border-slate-100">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${isPrimary ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                        <SafeIcon icon={FiServer} />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{provider.name}</h3>
                        <p className="text-xs text-slate-500 mt-1">{provider.status}</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{provider.mode}</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed mt-4 min-h-[36px]">{provider.notes || 'No notes.'}</p>
                </div>

                <div className="p-4 grid grid-cols-2 gap-2">
                  <button onClick={() => testProvider(provider)} className="py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-bold hover:bg-slate-50 flex items-center justify-center gap-2">
                    <SafeIcon icon={FiSend} /> Test Send
                  </button>
                  {!isPrimary && (
                    <button onClick={() => updateMode(provider, 'Primary')} className="py-2.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 flex items-center justify-center gap-2">
                      <SafeIcon icon={FiCheckCircle} /> Make Primary
                    </button>
                  )}
                                    {isPrimary && (
                    <div className="py-2.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold flex items-center justify-center gap-2">
                      <SafeIcon icon={FiCheckCircle} /> Active Route
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {!providers.length && (
            <div className="md:col-span-2 xl:col-span-3 bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
              No providers configured.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Providers;